/**
 * Lista de issues de Plane para la página de detalle del proyecto.
 *
 * Reemplazó una flat list que se volvía inutilizable cuando el proyecto tenía
 * 50+ issues (caso típico: Kiply). Ahora:
 *   - Búsqueda por nombre.
 *   - Filtros chip por state group (Backlog/Unstarted/Started/Completed/Cancelled)
 *     con counts. Por default ocultamos completed+cancelled.
 *   - Filtros chip por prioridad.
 *   - Filas compactas (icon de estado, badge de prioridad, asignados, due date).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  ExternalLink,
  Search,
  CheckCircle2,
  Circle,
  PauseCircle,
  XCircle,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import clsx from "clsx";
import type { PlaneIssueSummary, PlaneStateGroup, PlanePriority } from "@axorial/shared";
import { formatDate } from "@/lib/format";

const STATE_GROUPS: { id: PlaneStateGroup; label: string }[] = [
  { id: "backlog", label: "Backlog" },
  { id: "unstarted", label: "Por hacer" },
  { id: "started", label: "En curso" },
  { id: "completed", label: "Completado" },
  { id: "cancelled", label: "Cancelado" },
  { id: "unknown", label: "Sin estado" },
];

const PRIORITY_BADGE: Record<PlanePriority, string> = {
  urgent: "badge-error",
  high: "badge-warning",
  medium: "badge-info",
  low: "badge-ghost",
  none: "badge-ghost",
};

const PRIORITY_LABEL: Record<PlanePriority, string> = {
  urgent: "Urgente",
  high: "Alta",
  medium: "Media",
  low: "Baja",
  none: "—",
};

const PRIORITY_ORDER: Record<PlanePriority, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
  none: 4,
};

const STATE_GROUP_ORDER: Record<PlaneStateGroup, number> = {
  started: 0,
  unstarted: 1,
  backlog: 2,
  completed: 3,
  cancelled: 4,
  unknown: 5,
};

// Por default, ocultamos "completed" y "cancelled" — el usuario casi nunca
// necesita verlos en la lista de trabajo activo. Si todos los issues
// resultaron `unknown` (fallback cuando no podemos resolver state group),
// los incluimos para no mostrar lista vacía.
function pickDefaultGroups(issues: PlaneIssueSummary[]): Set<PlaneStateGroup> {
  const base = new Set<PlaneStateGroup>(["backlog", "unstarted", "started"]);
  const allUnknown = issues.length > 0 && issues.every((it) => it.stateGroup === "unknown");
  if (allUnknown) base.add("unknown");
  return base;
}

const COMPACT_LIMIT = 12;
const PAGE_SIZE = 25;

/**
 * Map plane_user_id → user del panel resuelto por email. Si una persona
 * tiene cuenta en Plane pero NO en el panel (Google Workspace), no aparece
 * aquí — el row la mostrará como "externo".
 */
export type PlaneUsersMap = Record<string, { id: string; name: string; email: string }>;

export interface IssuesListProps {
  issues: PlaneIssueSummary[];
  users?: PlaneUsersMap;
  /**
   * Si true, recorta la lista a {@link COMPACT_LIMIT} sin paginación y
   * muestra link a la vista dedicada cuando el filtro deja más items.
   * Útil para preview embebido en la página de detalle del proyecto.
   */
  compact?: boolean;
  /** Ruta de la vista dedicada (solo se usa cuando `compact`). */
  projectIdForLink?: string;
}

export function IssuesList({
  issues,
  users = {},
  compact = false,
  projectIdForLink,
}: IssuesListProps) {
  const [query, setQuery] = useState("");
  const [visibleGroups, setVisibleGroups] = useState<Set<PlaneStateGroup>>(() =>
    pickDefaultGroups(issues),
  );
  const [onlyHighPriority, setOnlyHighPriority] = useState(false);
  const [page, setPage] = useState(0);

  // Reset paginación cada vez que cambia algo del filtro — si no, el usuario
  // se queda en una página que ya no existe.
  useEffect(() => {
    setPage(0);
  }, [query, visibleGroups, onlyHighPriority]);

  const groupCounts = useMemo(() => {
    const counts: Record<PlaneStateGroup, number> = {
      backlog: 0,
      unstarted: 0,
      started: 0,
      completed: 0,
      cancelled: 0,
      unknown: 0,
    };
    for (const it of issues) counts[it.stateGroup] += 1;
    return counts;
  }, [issues]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return issues
      .filter((it) => visibleGroups.has(it.stateGroup))
      .filter((it) => !onlyHighPriority || it.priority === "urgent" || it.priority === "high")
      .filter((it) => !q || it.name.toLowerCase().includes(q))
      .toSorted((a, b) => {
        const sg = STATE_GROUP_ORDER[a.stateGroup] - STATE_GROUP_ORDER[b.stateGroup];
        if (sg !== 0) return sg;
        const pr = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
        if (pr !== 0) return pr;
        return a.name.localeCompare(b.name);
      });
  }, [issues, query, visibleGroups, onlyHighPriority]);

  // En compact recortamos a un preview fijo; en vista dedicada paginamos.
  const totalFiltered = filtered.length;
  const totalPages = compact ? 1 : Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const visible = useMemo(() => {
    if (compact) return filtered.slice(0, COMPACT_LIMIT);
    const start = safePage * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, compact, safePage]);

  const grouped = useMemo(() => {
    const map = new Map<PlaneStateGroup, PlaneIssueSummary[]>();
    for (const it of visible) {
      if (!map.has(it.stateGroup)) map.set(it.stateGroup, []);
      map.get(it.stateGroup)!.push(it);
    }
    return map;
  }, [visible]);

  const hiddenInCompact = compact ? Math.max(0, totalFiltered - COMPACT_LIMIT) : 0;

  const toggleGroup = (g: PlaneStateGroup) => {
    setVisibleGroups((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });
  };

  if (issues.length === 0) {
    return <p className="text-sm text-base-content/60 py-2">Sin issues abiertos en Plane.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-2">
        <label className="input input-bordered input-sm flex items-center gap-2 flex-1 min-w-0">
          <Search className="size-3.5 text-base-content/40 shrink-0" />
          <input
            type="text"
            className="grow min-w-0"
            placeholder="Buscar por nombre…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="text-base-content/40 hover:text-base-content text-xs"
              aria-label="Limpiar búsqueda"
            >
              ✕
            </button>
          )}
        </label>
        <button
          type="button"
          onClick={() => setOnlyHighPriority((v) => !v)}
          className={clsx(
            "btn btn-sm",
            onlyHighPriority ? "btn-warning" : "btn-ghost border border-base-300/60",
          )}
        >
          🔥 Urgente / alta
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {STATE_GROUPS.map(({ id, label }) => {
          const count = groupCounts[id];
          if (count === 0) return null;
          const active = visibleGroups.has(id);
          return (
            <button
              key={id}
              type="button"
              onClick={() => toggleGroup(id)}
              className={clsx(
                "px-2.5 py-1 rounded-full text-xs font-medium border transition-colors",
                active
                  ? "bg-primary/15 border-primary/40 text-primary"
                  : "bg-base-200/50 border-base-300/60 text-base-content/55 hover:bg-base-200",
              )}
              aria-pressed={active}
            >
              {label} <span className="tabular-nums opacity-70">({count})</span>
            </button>
          );
        })}
      </div>

      {totalFiltered === 0 ? (
        <p className="text-sm text-base-content/55 italic py-3">
          Ningún issue coincide con los filtros actuales.
        </p>
      ) : (
        <div className="space-y-3">
          {STATE_GROUPS.map(({ id, label }) => {
            const items = grouped.get(id);
            if (!items?.length) return null;
            const totalInGroup = filtered.filter((it) => it.stateGroup === id).length;
            return (
              <section key={id} className="space-y-1">
                <h4 className="text-[11px] font-semibold uppercase tracking-wider text-base-content/50 px-1">
                  {label}{" "}
                  <span className="tabular-nums opacity-60">
                    · {items.length}
                    {items.length < totalInGroup && ` de ${totalInGroup}`}
                  </span>
                </h4>
                <ul className="rounded-md border border-base-300/50 divide-y divide-base-300/40 bg-base-100">
                  {items.map((it) => (
                    <IssueRow key={it.id} issue={it} users={users} />
                  ))}
                </ul>
              </section>
            );
          })}

          {compact && hiddenInCompact > 0 && projectIdForLink && (
            <Link
              to="/projects/$projectId/issues"
              params={{ projectId: projectIdForLink }}
              className="btn btn-ghost btn-sm w-full justify-between mt-2"
            >
              <span>
                Ver los {totalFiltered}{" "}
                <span className="text-base-content/55">({hiddenInCompact} más ocultos)</span>
              </span>
              <ArrowRight className="size-3.5" />
            </Link>
          )}

          {!compact && totalPages > 1 && (
            <Pagination
              page={safePage}
              totalPages={totalPages}
              total={totalFiltered}
              pageSize={PAGE_SIZE}
              onChange={setPage}
            />
          )}
        </div>
      )}
    </div>
  );
}

function Pagination({
  page,
  totalPages,
  total,
  pageSize,
  onChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onChange: (next: number) => void;
}) {
  const start = page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, total);
  return (
    <div className="flex items-center justify-between gap-2 pt-2 border-t border-base-300/40">
      <div className="text-xs text-base-content/55 tabular-nums">
        Mostrando {start}–{end} de {total}
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onChange(page - 1)}
          disabled={page === 0}
          className="btn btn-ghost btn-xs btn-square"
          aria-label="Página anterior"
        >
          <ChevronLeft className="size-4" />
        </button>
        <span className="text-xs tabular-nums px-2 text-base-content/70">
          {page + 1} / {totalPages}
        </span>
        <button
          type="button"
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages - 1}
          className="btn btn-ghost btn-xs btn-square"
          aria-label="Página siguiente"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>
    </div>
  );
}

function IssueRow({ issue, users }: { issue: PlaneIssueSummary; users: PlaneUsersMap }) {
  const showPriority = issue.priority === "urgent" || issue.priority === "high";
  return (
    <li className="px-3 py-2 flex items-center gap-3 hover:bg-base-200/50 transition-colors">
      <StateIcon stateGroup={issue.stateGroup} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{issue.name}</div>
        <div className="text-xs text-base-content/55 mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="text-base-content/50">{issue.state}</span>
          {issue.dueDate && <span>· vence {formatDate(issue.dueDate)}</span>}
        </div>
      </div>
      {issue.assignees.length > 0 && <AssigneeAvatars assignees={issue.assignees} users={users} />}
      {showPriority && (
        <span className={clsx("badge badge-xs shrink-0", PRIORITY_BADGE[issue.priority])}>
          {PRIORITY_LABEL[issue.priority]}
        </span>
      )}
      <a
        href={issue.url}
        target="_blank"
        rel="noreferrer"
        className="btn btn-ghost btn-xs btn-square shrink-0 text-base-content/40 hover:text-primary"
        aria-label="Abrir en Plane"
      >
        <ExternalLink className="size-3.5" />
      </a>
    </li>
  );
}

function AssigneeAvatars({ assignees, users }: { assignees: string[]; users: PlaneUsersMap }) {
  const MAX_VISIBLE = 3;
  const visible = assignees.slice(0, MAX_VISIBLE);
  const overflow = assignees.length - visible.length;

  return (
    <div className="flex items-center -space-x-1.5 shrink-0">
      {visible.map((planeId) => {
        const user = users[planeId];
        const label = user?.name ?? "Externo";
        const title = user
          ? `${user.name} (${user.email})`
          : "Asignado externo (sin cuenta en el panel)";
        return (
          <span
            key={planeId}
            title={title}
            className={clsx(
              "size-6 rounded-full grid place-items-center text-[10px] font-semibold ring-2 ring-base-100",
              user
                ? "bg-primary/15 text-primary"
                : "bg-base-200 text-base-content/45 border border-dashed border-base-content/25",
            )}
          >
            {avatarInitials(label)}
          </span>
        );
      })}
      {overflow > 0 && (
        <span
          title={`${overflow} más`}
          className="size-6 rounded-full grid place-items-center text-[10px] font-medium bg-base-200 text-base-content/65 ring-2 ring-base-100"
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}

function avatarInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

function StateIcon({ stateGroup }: { stateGroup: PlaneStateGroup }) {
  if (stateGroup === "completed") return <CheckCircle2 className="size-4 text-success shrink-0" />;
  if (stateGroup === "cancelled")
    return <XCircle className="size-4 text-base-content/30 shrink-0" />;
  if (stateGroup === "started") return <PauseCircle className="size-4 text-warning shrink-0" />;
  return <Circle className="size-4 text-base-content/35 shrink-0" />;
}
